//@flow

import * as React from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import ButtonWithPopover from './ButtonWithPopover';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum
} from 'common/components/core/popover/popOverFactory';
import defaultTheme from './SplitButton.scss';

type Props = {
    onPopoverHide: () => void,
    popOverClassName: ?string,
    tooltip: ?string,
    getExportPopup: ({
        showEmail: boolean,
        closePopover: string => void
    }) => React.Element<any>,
    isLoading: boolean,
    disabled: boolean,
    disableArrow: boolean,
    invert: boolean,
    isPopOverOpen: boolean,
    showDropdown: boolean,
    isVisible: boolean,
    shouldOpenPopupOnLabelledButton: boolean,
    hidePopoverOnClick: boolean,
    label: ?string,
    buttonType: ?string,
    onClick: () => void,
    theme: { +[key: ?string]: string }
};

type State = {
    showPopUpOnLabelledButton: boolean
};

class SplitButton extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            showPopUpOnLabelledButton: false
        };
    }

    handleLabelledButtonClick = () => {
        this.setState({ showPopUpOnLabelledButton: true });
    };

    handleLabelledButtonPopoverHide = () => {
        const { onPopoverHide } = this.props;
        this.setState({ showPopUpOnLabelledButton: false }, () => {
            onPopoverHide();
        });
    };

    getLabelledButtonConditionalProps = () => {
        const { getExportPopup, popOverClassName, tooltip } = this.props;
        const { showPopUpOnLabelledButton } = this.state;

        if (showPopUpOnLabelledButton) {
            return {
                popOverContent: getExportPopup({
                    showEmail: false,
                    closePopover: this.handleClosePopover.bind(
                        this,
                        this.labelledButtonPopoverRef
                    )
                }),
                triggerPopoverOn: PopoverTriggerEnum.Click,
                onHide: this.handleLabelledButtonPopoverHide,
                popOverClassName,
                disableArrow: true
            };
        }
        return {
            triggerPopoverOn: PopoverTriggerEnum.Hover,
            popOverContent: tooltip
        };
    };

    handleClosePopover = ref => {
        if (ref) {
            ref.getWrappedInstance().hidePopover();
        }
    };

    getLabelledButtonPopoverRef = ref => {
        if (ref) {
            this.labelledButtonPopoverRef = ref;
        }
    };

    getDropdownButtonPopoverRef = ref => {
        if (ref) {
            this.dropdownButtonPopoverRef = ref;
        }
    };

    render() {
        const {
            onClick,
            theme,
            isLoading,
            disabled,
            buttonType,
            label,
            disableArrow,
            popOverClassName,
            hidePopoverOnClick,
            invert,
            isPopOverOpen,
            showDropdown,
            isVisible,
            onPopoverHide,
            getExportPopup,
            shouldOpenPopupOnLabelledButton,
            'data-test-id': dataTestId
        } = this.props;

        return (
            <div className={theme.splitButton}>
                <ButtonWithPopover
                    data-test-id={dataTestId}
                    uppercase
                    smallLoader
                    semibold
                    ripple={false}
                    color="off-white"
                    borderColor="cloud-grey"
                    theme={theme}
                    className={classnames(theme.splitTooltipButton, {
                        [theme.hideRightBorderRadius]: showDropdown
                    })}
                    ref={this.getLabelledButtonPopoverRef}
                    invert={invert}
                    isLoading={isLoading}
                    onClick={
                        shouldOpenPopupOnLabelledButton
                            ? this.handleLabelledButtonClick
                            : onClick
                    }
                    disabled={disabled}
                    buttonType={buttonType}
                    label={label}
                    preferPlace={PopoverPlaceEnum.Above}
                    {...this.getLabelledButtonConditionalProps()}
                />
                {showDropdown ? (
                    <ButtonWithPopover
                        invertPopover
                        disableAnimation
                        enableAntialias
                        circle={false}
                        ripple={false}
                        icon="caret_up"
                        borderColor="cloud-grey"
                        color="off-white"
                        theme={theme}
                        className={classnames(
                            theme.splitPopover,
                            theme.splitIconBtn
                        )}
                        ref={this.getDropdownButtonPopoverRef}
                        disabled={disabled}
                        popOverClassName={popOverClassName}
                        triggerPopoverOn={PopoverTriggerEnum.Click}
                        place={PopoverPlaceEnum.top}
                        popOverContent={getExportPopup({
                            showEmail: true,
                            closePopover: this.handleClosePopover.bind(
                                this,
                                this.dropdownButtonPopoverRef
                            )
                        })}
                        isPopOverOpen={isPopOverOpen}
                        isVisible={isVisible}
                        buttonType={buttonType}
                        disableArrow={disableArrow}
                        hidePopoverOnClick={hidePopoverOnClick}
                        onHide={onPopoverHide}
                    />
                ) : null}
            </div>
        );
    }
}

const ThemedSplitButton = themr('SplitButton', defaultTheme, { withRef: true })(
    SplitButton
);
export { ThemedSplitButton as SplitButton };
